import React, {
  CSSProperties,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const VIEWPORT_MARGIN = 12;

interface ActivityStepsPanelPosition {
  top: number;
  left: number;
  width: number;
}

export function ActivityStepsPreview({
  activityName,
  activitySteps,
  isOpen,
  onOpenChange,
}: {
  activityName: string;
  activitySteps: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const [panelPosition, setPanelPosition] =
    useState<ActivityStepsPanelPosition | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !rootRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        onOpenChange(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onOpenChange(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onOpenChange]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelPosition(null);
      return;
    }

    const updatePanelPosition = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;

      const triggerRect = trigger.getBoundingClientRect();
      const availableWidth = Math.max(
        0,
        window.innerWidth - VIEWPORT_MARGIN * 2,
      );
      const width = Math.min(triggerRect.width, availableWidth);
      panel.style.width = `${width}px`;
      const panelHeight = panel.getBoundingClientRect().height;
      const left = Math.min(
        Math.max(VIEWPORT_MARGIN, triggerRect.left),
        Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - width),
      );
      const top =
        triggerRect.top + panelHeight <= window.innerHeight - VIEWPORT_MARGIN
          ? Math.max(VIEWPORT_MARGIN, triggerRect.top)
          : Math.max(VIEWPORT_MARGIN, triggerRect.bottom - panelHeight);

      setPanelPosition({ top, left, width });
    };

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);
    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [activitySteps, isOpen]);

  const labelActivityName = activityName || "homework assignment";
  const portalRoot =
    rootRef.current?.closest(".clinician-portal") ?? document.body;

  return (
    <div
      ref={rootRef}
      className="cp-activity-steps-disclosure"
      data-open={isOpen || undefined}
    >
      <button
        ref={triggerRef}
        type="button"
        className="cp-activity-steps-disclosure__trigger"
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={`${isOpen ? "Hide" : "Show"} full Activity Steps for ${labelActivityName}`}
        onClick={() => onOpenChange(!isOpen)}
      >
        <span className="cp-activity-steps-preview">{activitySteps}</span>
      </button>
      {isOpen
        ? createPortal(
            <div
              ref={panelRef}
              className="cp-activity-steps-disclosure__panel"
              id={panelId}
              role="region"
              aria-label={`Full Activity Steps for ${labelActivityName}`}
              style={
                {
                  top: panelPosition?.top ?? 0,
                  left: panelPosition?.left ?? 0,
                  width: panelPosition?.width ?? 0,
                  visibility: panelPosition ? "visible" : "hidden",
                } as CSSProperties
              }
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                onOpenChange(false);
              }}
            >
              {activitySteps}
            </div>,
            portalRoot,
          )
        : null}
    </div>
  );
}
